
import {
  Component,
  OnInit,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges, OnDestroy, Output, EventEmitter, AfterViewInit
} from '@angular/core';
import * as c3 from 'c3';
import { GraphUtilsService } from '../../services/graph-utils.service';
import { DataService } from '../../services/data.service';

@Component({
    selector: 'app-c3-ega-time-series',
    templateUrl: './c3-ega-time-series.component.html',
    styleUrls: ['./c3-ega-time-series.component.scss'],
    standalone: false
})
export class C3EgaTimeSeriesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() data: any;
  @Input() gakpi: any;
  @Input() filter: any;
  @Input() uslLsl: boolean;
  @Input() paramName: any;
  @Input() trends: any;
  @Output() pointSelection: EventEmitter<object> = new EventEmitter();
  @Input() yzoom: any;

  gachart: any = null;
  colors: any = {};
  subscription: any;
  selectedPoint: any[] = [];

  constructor(
    private elRef: ElementRef,
    private graphUtil: GraphUtilsService,
    private dataSer: DataService
  ) {

    this.subscription = dataSer.events.subscribe(this.replotGraphEventSubscription);
  }

  replotGraphEventSubscription = (res: any) => {
    if (res.name === 'replotGraph') {
      let timeoutval = 0;
      if (res.args && res.args[0] === 'menuToggle') {
        timeoutval = 101;
      }
      setTimeout(() => {
        this.checkAndReplotGraph();
      }, timeoutval);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //  changed to fix memory leak
    if (this.gachart) {
      this.gachart.destroy();
    }
    this.checkAndReplotGraph();
  }

  ngOnInit() {
    this.colors = this.graphUtil.getColorList();
  }

  ngAfterViewInit(): void {
    this.checkAndReplotGraph();
  }

  checkAndReplotGraph() {
    if (this.data && this.data.length) {
      this.generateChart(this.data);
      this.selectedPoint = [];
    } else {
      this.gachart = null;
    }
  }

  generateChart = (data) => {
    const bounds = this.findBoundaryVals(this.data, 'value', this.gakpi);
    this.gachart = c3.generate({
      bindto: this.elRef.nativeElement.children[0].children[0],
      padding: {
        right: 24,
        top: 10
      },
      legend: {
        show: false
      },
      data: {
        x: 'time',
        columns: [
          ['time', ...this.data.map(elem => elem.convertedTS)],
          [this.paramName, ...this.data.map(elem => elem.value)],
        ],
        type: 'line',
        color: (d: any, i: c3.DataPoint) => this.colorGenerate(d, i),
        selection: {
          enabled: !this.filter.outliers && !this.trends,
          multiple: true
        },
        onselected: (d) => {
          this.onSelectPoint(d);
        },
        onunselected: (d) => {
          this.onUnSelectPoint(d);
        }
      },
      transition: {
        duration: 0
      },
      point: {
        r: 3
      },
      axis: {
        x: {
          type: 'timeseries',
          tick: {
            format: '%_d %b %y %H:%M',
            multiline: true
          },
        },
        y: {
          max: this.yzoom.max ? this.yzoom.max : bounds.max,
          min: this.yzoom.min ? this.yzoom.min : bounds.min
        }
      },
      zoom: {
        type: 'scroll',
        enabled: true
      },
      grid: {
        lines: {
          front: false
        }
      },
    });
    this.addThresholdLines(this.gachart);
  }

  findBoundaryVals(dataArray: any, key, gakpi, bufferFactor = 0.03) {
    const boundaryVals = {
      max: -999999,
      min: 999999
    };

    dataArray.forEach(element => {
      if (element[key] >= boundaryVals.max) {
        boundaryVals.max = element[key];
      }
      if (element[key] <= boundaryVals.min) {
        boundaryVals.min = element[key];
      }
    });
    if (gakpi) {
      if (gakpi.usl !== null && boundaryVals.max < gakpi.usl) {
        boundaryVals.max = gakpi.usl;
      }
      if (gakpi.lsl !== null && boundaryVals.min > gakpi.lsl) {
        boundaryVals.min = gakpi.lsl;
      }
    }
    if (gakpi && (gakpi.usl || gakpi.lsl)) {
      return {
        max: Math.ceil(boundaryVals.max * (boundaryVals.max >= 0 ? (1 + bufferFactor) : (1 - bufferFactor))),
        min: Math.floor(boundaryVals.min * (boundaryVals.min >= 0 ? (1 - bufferFactor) : (1 + bufferFactor))),
      };
    } else {
      return {
        max: boundaryVals.max < gakpi.targetGA ? gakpi.targetGA : boundaryVals.max,
        min: boundaryVals.min > gakpi.targetGA ? gakpi.targetGA : boundaryVals.min
      };
    }
  }

  addThresholdLines(chart) {
    const targetValue = this.gakpi.targetValue ? this.gakpi.targetValue : this.gakpi.targetGA;
    const targetLabel = this.gakpi.targetValue ? 'TARGET' : 'TARGET GA';
    if (this.uslLsl) {
      chart.ygrids.add([
        { value: this.gakpi.usl ? this.gakpi.usl : null, text: 'USL', class: 'specification-limit' },
        { value: this.gakpi.lsl ? this.gakpi.lsl : null, text: 'LSL', class: 'specification-limit' },
      ]);
    }
    chart.ygrids.add([
      { value: targetValue, text: targetLabel, class: 'target dashed' },
    ]);
  }

  colorGenerate(d: any, i: any) {
    let color = '#0085ca';
    if (this.filter.outliers) {
      color = this.colors.normal;
    }
    if (this.trends) {
      color = '#29c629';
    }
    return color;
  }

  onUnSelectPoint(selectedpoint) {
    if (selectedpoint && (selectedpoint.index || selectedpoint.index === 0)) {
      this.selectedPoint = this.selectedPoint.filter((eachPoint) => eachPoint.indexInData !== selectedpoint.index);
      this.pointSelection.emit(this.selectedPoint);
    }
  }

  onSelectPoint(selectedpoint) {
    if (selectedpoint && (selectedpoint.index || selectedpoint.index === 0)) {
      const newselectedPoint = this.data[selectedpoint.index];
      newselectedPoint.indexInData = selectedpoint.index;
      this.selectedPoint.push(newselectedPoint);
      this.pointSelection.emit(this.selectedPoint);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
